import {
  Box,
  Button,
  Grid2,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { ViewInAr } from "@mui/icons-material";
import Map from "../../components/map/map.component";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import FieldInfoCard from "../../components/fieldCard/fieldInfoCard.component";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchSensorsStart } from "../../store/features/customer/instruments/sensorsSlice";
import CustomAlert from "../../components/alert/alert.component";
import { fetchFieldDetailStart } from "../../store/features/customer/field/fieldSlice";
import { fetchFieldsStart } from "../../store/features/customer/fields/fieldsSlice";
import { useIsMobile } from "../../hook/useIsMobile";
import SensorChartPanel from "../../components/panel/sensorChartPanel.component";
import SensorAverageChartPanel from "../../components/panel/sensorAverageChartPanel.component";
import FieldMonitoring from "../../components/diseaseRisk/fieldMonitoring.component";
import FieldNoteContent from "../../components/fieldNoteContent/fieldNoteContent.component";
import { fetchFieldNotesStart } from "../../store/features/fieldNotes/fieldNotesSlice";
import { getFieldPmtilesUrl } from "../../utils/fieldMediaUtils";
import {
  fetchDiseaseStatusImageDetailStart,
  fetchDiseaseStatusStart,
  resetDiseaseStatusState,
} from "../../store/features/customer/diseaseStatus/diseaseStatusSlice";
import {
  FIELD_STATUS_ALL_CLASSIFICATIONS,
  resolveFieldDataFieldFilters,
  resolveFieldUuid,
} from "../../utils/fieldDataDiseaseStatusUtils";
import {
  fetchSensorHeatmapStart, resetSensorHeatmapState,
} from "../../store/features/customer/sensorHeatmap/sensorHeatmapSlice";
import { getLastCompleteHeatmapHour } from "../../utils/sensorHeatmapUtils";
import DiseaseImageDetailDialog from "../../components/map/diseaseImageDetailDialog.component";
import FieldIframeDialog from "../../components/muiDialogs/fieldIframeDialog.component";
import { getFieldIframeUrl } from "../../utils/fieldIframeUtils";
import {
  filterSensorsByField,
  getSharedMeasures,
  isWeatherStation,
} from "../../config/sensors.config";

const FieldPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isMobile = useIsMobile();
  const mapSectionRef = useRef(null);

  const accessToken = useSelector((state) => state?.auth?.user?.access_token);
  const fieldFromStore = useSelector((state) => state.field?.data?.data);
  const field =
    fieldFromStore && String(fieldFromStore.id) === String(id)
      ? fieldFromStore
      : null;
  const sensorsFromStore = useSelector((state) => state.sensors?.data?.data);
  const fieldsState = useSelector((state) => state.fields);
  const fields = fieldsState?.data?.data;
  const { isActive } = useSelector((state) => state.dragMode);
  const { isLoading } = useSelector((state) => state.sensors);
  const { isLocationActive } = useSelector((state) => state.dragMode);
  const fieldNotes = useSelector((state) => state.fieldNotes?.data?.data);
  const diseaseStatus = useSelector((state) => state.diseaseStatus);
  const sensorHeatmap = useSelector((state) => state.sensorHeatmap);
  const fieldUuid = resolveFieldUuid(field);
  const pmtilesUrl = getFieldPmtilesUrl(field);
  const fieldFromList = useMemo(
    () =>
      Array.isArray(fields)
        ? fields.find((item) => String(item?.id) === String(id)) || null
        : null,
    [fields, id],
  );
  const sensors = useMemo(() => {
    return filterSensorsByField(
      sensorsFromStore,
      id,
      field?.sensors || fieldFromList?.sensors,
    );
  }, [sensorsFromStore, field, fieldFromList, id]);
  const weatherSensors = useMemo(
    () => sensors.filter(isWeatherStation),
    [sensors],
  );
  const ordinarySensors = useMemo(
    () => sensors.filter((sensor) => !isWeatherStation(sensor)),
    [sensors],
  );
  // The average API distinguishes station temperature from sensor temperatures by measure name.
  const sharedMeasures = useMemo(
    () => ({
      weather: getSharedMeasures(weatherSensors, ordinarySensors)
        .filter((measure) => !["air_temperature", "temperature"].includes(measure)),
      sensors: getSharedMeasures(ordinarySensors, weatherSensors)
        .filter((measure) => !["air_temperature", "soil_temperature"].includes(measure)),
    }),
    [weatherSensors, ordinarySensors],
  );
  const fieldIframeUrl =
    getFieldIframeUrl(field) || getFieldIframeUrl(fieldFromList);
  const fieldDataFilters = useMemo(
    () => resolveFieldDataFieldFilters(field),
    [field],
  );
  const fieldPolygon = field?.polygon;
  const hasFieldPolygon =
    Array.isArray(fieldPolygon) && fieldPolygon.length > 0;
  const shouldRenderMap = Boolean(field && (hasFieldPolygon || pmtilesUrl));

  const [hasActiveFilters, setHasActiveFilter] = useState(false);
  const [isFieldIframeOpen, setIsFieldIframeOpen] = useState(false);
  const [selectedDiseaseImageId, setSelectedDiseaseImageId] = useState(null);
  const [mapSelection, setMapSelection] = useState({
    fieldId: id,
    mode: "sensors",
  });
  const mapMode =
    mapSelection.fieldId === id && !isActive ? mapSelection.mode : "sensors";
  const setMapMode = useCallback(
    (mode) => setMapSelection((current) =>
      current.fieldId === id && current.mode === mode
        ? current
        : { fieldId: id, mode },
    ),
    [id],
  );
  const isFieldStatusEnabled = mapMode === "field-status";
  const isHeatmapEnabled = mapMode === "heatmap" && Boolean(fieldUuid);
  const [fieldStatusClassification, setFieldStatusClassification] = useState(
    FIELD_STATUS_ALL_CLASSIFICATIONS,
  );
  const showEmptyFilterResult = fieldNotes?.length === 0 && hasActiveFilters;
  const selectedDiseaseImageDetail = selectedDiseaseImageId
    ? diseaseStatus?.detailsById?.[selectedDiseaseImageId]
    : null;
  const selectedDiseaseImageLoading = selectedDiseaseImageId
    ? diseaseStatus?.detailsLoadingById?.[selectedDiseaseImageId]
    : false;
  const selectedDiseaseImageError = selectedDiseaseImageId
    ? diseaseStatus?.detailErrorsById?.[selectedDiseaseImageId]
    : null;

  const transformedSensors = useMemo(
    () =>
      Array.isArray(sensors)
        ? sensors
            .filter(
              (sensor) =>
                Array.isArray(sensor?.gps_position) &&
                sensor.gps_position.length === 2,
            )
            .map((sensor) => ({
              ...sensor,
              latitude: sensor.gps_position[0],
              longitude: sensor.gps_position[1],
            }))
        : [],
    [sensors],
  );

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    dispatch(fetchSensorsStart({ accessToken: accessToken, field: id }));
  }, [dispatch, accessToken, id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(fetchFieldNotesStart({ field: id }));

    if (!accessToken) {
      return;
    }

    dispatch(fetchFieldDetailStart({ accessToken: accessToken, fieldId: id }));
  }, [accessToken, dispatch, id]);

  useEffect(() => {
    if (
      !accessToken ||
      Array.isArray(fields) ||
      fieldsState?.isLoading ||
      fieldsState?.error
    ) {
      return;
    }

    dispatch(fetchFieldsStart({ accessToken }));
  }, [accessToken, dispatch, fields, fieldsState?.error, fieldsState?.isLoading]);

  useEffect(() => {
    if (!isFieldStatusEnabled || fieldDataFilters.length === 0) {
      dispatch(resetDiseaseStatusState());
      return;
    }

    dispatch(
      fetchDiseaseStatusStart({
        fieldFilters: fieldDataFilters,
        classification: fieldStatusClassification,
        fieldPolygon,
      }),
    );
  }, [
    dispatch,
    fieldPolygon,
    fieldDataFilters,
    fieldStatusClassification,
    isFieldStatusEnabled,
  ]);

  useEffect(() => {
    setSelectedDiseaseImageId(null);
    setIsFieldIframeOpen(false);
    setMapMode("sensors");
    setFieldStatusClassification(FIELD_STATUS_ALL_CLASSIFICATIONS);
  }, [id, setMapMode]);

  useEffect(() => {
    if (isActive) setMapMode("sensors");
  }, [isActive, setMapMode]);

  useEffect(() => {
    if (!isHeatmapEnabled) {
      dispatch(resetSensorHeatmapState());
      return undefined;
    }
    dispatch(
      fetchSensorHeatmapStart({
        fieldUuid,
        measurementName: "temperature",
        timestamp: getLastCompleteHeatmapHour(),
        retryOnEmpty: true,
      }),
    );
    return () => dispatch(resetSensorHeatmapState());
  }, [dispatch, fieldUuid, isHeatmapEnabled]);

  const handleHeatmapApply = useCallback(
    ({ measurementName, timestamp }) => {
      if (!fieldUuid || !isHeatmapEnabled) return;
      dispatch(fetchSensorHeatmapStart({ fieldUuid, measurementName, timestamp }));
    },
    [dispatch, fieldUuid, isHeatmapEnabled],
  );

  useEffect(() => {
    if (!isActive || !isMobile) {
      return;
    }

    mapSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [isActive, isMobile]);

  const handleDiseaseImageClick = useCallback(
    (imageId) => {
      setSelectedDiseaseImageId(imageId);

      if (!diseaseStatus?.detailsById?.[imageId]) {
        dispatch(fetchDiseaseStatusImageDetailStart({ imageId }));
      }
    },
    [diseaseStatus?.detailsById, dispatch],
  );

  const handleDiseaseImageDialogClose = () => {
    setSelectedDiseaseImageId(null);
  };

  return (
    <Box width="100%" height={isMobile ? "auto" : "100%"}>
      {isActive && !isLocationActive && (
        <CustomAlert
          severity="info"
          title="Trascina sensore"
          theme={theme}
          message="Trascina il tuo sensore nella posizione corretta"
          bottom={null}
          top={150}
          left="40%"
        />
      )}
      <Grid2 container height="100%">
        <Grid2
          ref={mapSectionRef}
          size={{ xs: 12, md: 8 }}
          height={isMobile ? "70vh" : "100%"}
          width="100%"
          position="relative"
        >
          {shouldRenderMap ? (
            <Map
              fieldPosition={
                hasFieldPolygon ? fieldPolygon[0] : field?.position
              }
              polygonCoordinates={hasFieldPolygon ? fieldPolygon : []}
              pmtilesUrl={pmtilesUrl}
              subAreas={field?.field_parts}
              markers={transformedSensors}
              showFieldStatusControl
              isFieldStatusEnabled={isFieldStatusEnabled}
              onFieldStatusEnabledChange={isActive ? undefined : (enabled) =>
                setMapMode(enabled ? "field-status" : "sensors")
              }
              heatmap={{
                enabled: isHeatmapEnabled,
                status: sensorHeatmap?.status || "idle",
                error: sensorHeatmap?.error,
                data: sensorHeatmap?.data?.field_uuid === fieldUuid
                  ? sensorHeatmap.data
                  : null,
                disabledReason: isActive
                  ? "Heatmap non disponibile durante il trascinamento"
                  : !fieldUuid ? "UUID del terreno non disponibile" : null,
                onEnabledChange: (enabled) => {
                  if (enabled && (!fieldUuid || isActive)) return;
                  setMapMode(enabled ? "heatmap" : "sensors");
                },
                onApply: handleHeatmapApply,
              }}
              fieldStatusClassification={fieldStatusClassification}
              onFieldStatusClassificationChange={setFieldStatusClassification}
              diseaseStatusImages={diseaseStatus?.data?.images}
              diseaseStatusClassificationCounts={
                diseaseStatus?.data?.classificationCounts
              }
              fieldNoteDiseaseReports={fieldNotes}
              onDiseaseImageClick={handleDiseaseImageClick}
            />
          ) : isLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
            />
          ) : null}
          {fieldIframeUrl && (
            <Button
              variant="contained"
              startIcon={<ViewInAr />}
              onClick={() => setIsFieldIframeOpen(true)}
              aria-haspopup="dialog"
              aria-controls="field-iframe-dialog"
              sx={{
                position: "absolute",
                top: { xs: "auto", md: 16 },
                bottom: { xs: 104, md: "auto" },
                right: { xs: 12, sm: 16 },
                zIndex: 1000,
                minHeight: "40px",
                px: 2,
                textTransform: "none",
                borderRadius: "2px",
                boxShadow: 2,
                whiteSpace: "nowrap",
              }}
            >
              Apri Dettaglio Satellitare
            </Button>
          )}
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }} height="100%" width="100%">
          <FieldInfoCard field={field} sensors={sensors} />
        </Grid2>
        {isMobile ? undefined : (
          <Box
            width="100%"
            height={isMobile ? "100vh" : "auto"}
            paddingY={2}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <FieldMonitoring key={id} field={field}>
              <Typography fontWeight="bold" fontSize="20px" mb={1}>
                Sensori
              </Typography>
              <SensorAverageChartPanel
                key={`${id}:sensors:average`}
                scope={`${id}:sensors:average`}
                source="sensors"
                title="Valori medi"
                subtitle="Seleziona le misure da mostrare nel grafico"
                sensors={ordinarySensors}
                unavailableMeasures={sharedMeasures.sensors}
                accessToken={accessToken}
                id={id}
              />
              <SensorChartPanel
                key={`${id}:sensors:current`}
                scope={`${id}:sensors:current`}
                sensors={ordinarySensors}
                accessToken={accessToken}
                title="Valori attuali"
                subtitle="Seleziona i sensori da mostrare nel grafico"
              />
              {weatherSensors.length > 0 && (
                <>
                  <Typography fontWeight="bold" fontSize="20px" mb={1}>
                    Stazione meteo
                  </Typography>
                  <SensorAverageChartPanel
                    key={`${id}:weather:average`}
                    scope={`${id}:weather:average`}
                    source="weather"
                    title="Valori medi"
                    subtitle="Seleziona le misure meteo da mostrare nel grafico"
                    sensors={weatherSensors}
                    unavailableMeasures={sharedMeasures.weather}
                    accessToken={accessToken}
                    id={id}
                  />
                  <SensorChartPanel
                    key={`${id}:weather:current`}
                    scope={`${id}:weather:current`}
                    title="Valori attuali"
                    subtitle="Seleziona le misure meteo da mostrare nel grafico"
                    sensors={weatherSensors}
                    accessToken={accessToken}
                    filtered={false}
                  />
                </>
              )}
            </FieldMonitoring>
            {fieldNotes?.length > 0 && (
              <Box height="100%">
                <Typography fontWeight="bold" fontSize="20px" mb={1}>
                  Taccuino di campo
                </Typography>
                <FieldNoteContent
                  filteredNotes={fieldNotes}
                  isFieldPage={true}
                  showEmptyFilterResult={showEmptyFilterResult}
                  onFiltersChange={setHasActiveFilter}
                  fieldId={id}
                />
              </Box>
            )}
          </Box>
        )}
      </Grid2>
      <DiseaseImageDetailDialog
        open={Boolean(selectedDiseaseImageId)}
        onClose={handleDiseaseImageDialogClose}
        detail={selectedDiseaseImageDetail}
        isLoading={Boolean(selectedDiseaseImageLoading)}
        error={selectedDiseaseImageError}
      />
      <FieldIframeDialog
        open={isFieldIframeOpen}
        onClose={() => setIsFieldIframeOpen(false)}
        url={fieldIframeUrl}
        fieldName={field?.name || fieldFromList?.name}
      />
    </Box>
  );
};

export default FieldPage;
